import { FormSection } from '@/api/rest/connectionApplication/useGetConnectionApplicationSchema';
import { Field, Select, ValidationMessages } from '@payaca/ambrosia';

import { sentenceCase } from '@payaca/utilities/stringUtilities';
import { FC, useEffect } from 'react';
import {
  Controller,
  get,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import FieldSet from './FieldSet';
import FieldSetArrayInnerCard from './FieldSetArrayInnerCard';
import FieldSetArrayOuterCard from './FieldSetArrayOuterCard';
import {
  getDeviceClassForDeviceType,
  getDeviceClassSchemaForDeviceType,
  getDeviceTypesFromSchemaSection,
  getSchemaDataForDevice,
} from './utils/devices';
import { fieldValidationState } from './utils/reactHookForm';

const FieldArray: FC<{
  schemaSection: FormSection;
  definitions: Record<string, any>;
  fieldKey: string;
}> = ({ definitions, schemaSection, fieldKey: key }) => {
  const form = useFormContext();
  const lcts = form.watch('lcts');
  const fieldArrayError = get(form.formState.errors, key);
  const deviceTypes = getDeviceTypesFromSchemaSection(schemaSection, key, lcts);
  const output = useWatch({
    control: form.control,
    name: key,
  });

  const fieldArray = useFieldArray({ control: form.control, name: key });

  // Ensure that the `deviceClass` for each field is set
  // If the deviceType changes, update the `deviceClass`
  useEffect(() => {
    if (output) {
      output.forEach((item: any, index: number) => {
        if (item.deviceType) {
          const newDeviceClass = getDeviceClassForDeviceType(
            schemaSection,
            item.deviceType
          );
          const deviceClassKey = `${key}.${index}.deviceClass`;

          const currentDeviceClass = form.getValues(deviceClassKey);
          if (currentDeviceClass !== newDeviceClass) {
            form.setValue(deviceClassKey, newDeviceClass, {
              shouldValidate: true,
            });
          }
        }
      });
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'schemaSection', 'form', and 'key'
  }, [output]);

  return (
    <FieldSetArrayOuterCard
      onAdd={() =>
        fieldArray.append({
          deviceType: null,
          deviceSysRef: null,
          deviceClass: null,
        })
      }
    >
      {fieldArray.fields.map((field: any, index: number) => {
        // namespace for field array index
        const namespace = `${key}.${index}`;
        const deviceType = field.deviceType;
        const deviceTypeName = `${namespace}.deviceType`;
        const item = getDeviceClassSchemaForDeviceType(
          schemaSection,
          deviceType || deviceTypes[0]
        );

        const deviceTypeField = item?.properties?.deviceType as
          | { title: string }
          | undefined;
        const schemaData = getSchemaDataForDevice(
          schemaSection,
          definitions,
          output?.[index]?.deviceType || null
        );

        const fieldError = get(
          form.formState.errors,
          `${namespace}.deviceClass`
        );

        const fieldProps = fieldError
          ? {
              validationState: {
                isValid: false,
                validationMessages: ['This field is required'],
              },
            }
          : {};

        return (
          <FieldSetArrayInnerCard
            key={field.id}
            onRemove={
              // conditionally allow remove dependent on a minItems limit
              schemaSection.minItems &&
              fieldArray.fields.length <= schemaSection.minItems
                ? undefined
                : () => fieldArray.remove(index)
            }
          >
            <Field.Legacy name={deviceTypeName} {...fieldProps}>
              <Field.Label>
                {sentenceCase(deviceTypeField?.title || '')}
              </Field.Label>
              <Controller
                control={form.control}
                name={deviceTypeName}
                render={({ field: { ref, onChange, ...rest } }) => (
                  <Select
                    {...rest}
                    options={deviceTypes.map((option: any) => ({
                      label: option,
                      value: option,
                    }))}
                    onChange={(change) => {
                      const newSchemaData = getSchemaDataForDevice(
                        schemaSection,
                        definitions,
                        change
                      );
                      if (deviceType !== change && newSchemaData) {
                        // deviceType has changed - reset the fields
                        Object.keys(newSchemaData.properties).forEach((key) => {
                          form.setValue(`${namespace}.${key}`, null);
                        });
                      }

                      // update deviceType
                      onChange(change);
                    }}
                  />
                )}
              />
            </Field.Legacy>

            {/* conditional fields based on deviceType */}
            {schemaData && (
              <FieldSet
                namespace={namespace}
                formSection={schemaData}
                definitions={definitions}
              />
            )}
          </FieldSetArrayInnerCard>
        );
      })}

      {!!fieldArrayError?.message && (
        <ValidationMessages
          validationState={fieldValidationState(fieldArrayError)}
        />
      )}
    </FieldSetArrayOuterCard>
  );
};
export default FieldArray;
